/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ K2 — SEEDS / ONBOARDING · pure model                                   │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * First launch as guest: app offers (NEVER forces) creating a sample list
 * teaching the domain. User decision is persisted in a local per-account
 * marker ("seeded" | "dismissed") to avoid re-offering.
 *
 * Key design (kit propagable across clones):
 *  - Seed is OPT-IN via dashboard card — not seeded at boot.
 *    Seeding automatically would break "clean guest = no
 *    data" contract of guest->account migration flow (the prompt "keep
 *    local data?" would fire on each login) and all e2e tests assuming an
 *    empty guest. If user REQUESTS sample, that data is theirs and the
 *    subsequent merge prompt is correct.
 *  - This module is pure (no DOM/DB): marker storage is injected
 *    (localStorage in web, any KV in native) and fault-tolerant
 *    (private mode / full quota degrade to "not persisted", never throw).
 */

export type OnboardingMarker = "seeded" | "dismissed";

/** Minimal KV compatible with `localStorage` (injectable in tests/native). */
export interface OnboardingMarkerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Per-account key: guest and each real account have own marker. */
export function onboardingMarkerKey(userId: string): string {
  return `k2:onboarding:${userId}`;
}

/**
 * Reads persisted marker. Tolerant: storage that throws (private mode) or
 * unknown values degrade to `null` (= not yet decided).
 */
export function readOnboardingMarker(
  storage: OnboardingMarkerStorage,
  userId: string,
): OnboardingMarker | null {
  try {
    const raw = storage.getItem(onboardingMarkerKey(userId));
    return raw === "seeded" || raw === "dismissed" ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Persists user decision. Returns `false` if storage failed (the
 * UI continues working in memory; simply re-offered after reload).
 */
export function writeOnboardingMarker(
  storage: OnboardingMarkerStorage,
  userId: string,
  marker: OnboardingMarker,
): boolean {
  try {
    storage.setItem(onboardingMarkerKey(userId), marker);
    return true;
  } catch {
    return false;
  }
}

export interface ShouldShowOnboardingInput {
  /** Active account = guest (real accounts may have data not yet synced). */
  isGuest: boolean;
  /**
   * `true`/`false` when emptiness queries resolved; `undefined`
   * while loading (do NOT show: avoids card flash for users with data).
   */
  hasData: boolean | undefined;
  /** Previous persisted decision, if any. */
  marker: OnboardingMarker | null;
}

/**
 * Onboarding card is shown ONLY to a pristine guest who has not
 * decided yet: no live data, no marker and with emptiness RESOLVED.
 */
export function shouldShowOnboarding(input: ShouldShowOnboardingInput): boolean {
  return input.isGuest && input.hasData === false && input.marker === null;
}
